import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderNoteDetailModel } from 'src/app/models/shopping/provider-note-detail.model';
import { ProviderNoteModel } from 'src/app/models/shopping/provider-note.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ProviderNoteDetailService } from 'src/app/services/shopping/provider-note-detail.service';
import { ProviderNoteService } from 'src/app/services/shopping/provider-note.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-provider-note-detail',
  templateUrl: './provider-note-detail.component.html',
  styleUrls: ['./provider-note-detail.component.scss'],
  standalone: false
})
export class ProviderNoteDetailComponent implements OnChanges {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly providerNoteService = inject(ProviderNoteService);
  private readonly providerNoteDetailService = inject(ProviderNoteDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() disableDetail: boolean = false;
  @Input('isFormView')
  set changeFormView(change: boolean) {
    if (change) this.createNewDetail();
  }
  @Input() permissionUpdatable: boolean = false;
  @Input() providerNoteModel: ProviderNoteModel = new ProviderNoteModel();
  @Input('updateListDetail')
  set changeUpdateListDetail(change: number) {
    if (change) this.getUpdateListDetail();
  }
  @Output() updateProviderNote = new EventEmitter<ProviderNoteModel>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<any> = new PagedResponseModel<any>();
  detailHeader: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  limit: number = 10;
  page: number = 1;
  providerNoteDetailModel: ProviderNoteDetailModel = new ProviderNoteDetailModel();
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];

  async ngOnChanges(changes: SimpleChanges) {
    const providerNoteModel = changes["providerNoteModel"];
    if (providerNoteModel && !providerNoteModel.firstChange) {
      await this.getColumnsForProviderNoteDetails();
      this.clearSelectionTrigger++;
      this.providerNoteDetailModel = new ProviderNoteDetailModel();
    }
  }

  createNewDetail() {
    this.providerNoteDetailModel = new ProviderNoteDetailModel();
    this.getFormView(true);
  }

  async deleteProviderNoteDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el detalle seleccionado?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerNoteDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle fue eliminado exitosamente.');
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de la nota proveedor.', error);
      }
    }
  }

  async getColumnsForProviderNoteDetails() {
    try {
      this.detailHeader = await firstValueFrom(this.providerNoteDetailService.getColumnsForProviderNoteDetails());
    } catch (error) {
      console.error('Error al buscar las columnas para el detalle de la nota proveedor:', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getProviderNote(providerNote: ProviderNoteModel) {
    this.updateProviderNote.emit(providerNote);
  }

  async getProviderNoteDetailsByProviderNoteId() {
    try {
      this.detailData = await firstValueFrom(this.providerNoteDetailService.getProviderNoteDetailsByProviderNoteId(
        this.page, this.limit, this.columnSort, this.ascendant, this.providerNoteModel.providerNoteId
      ));
    } catch (error) {
      console.error('Error al buscar los detalles de la nota proveedor.', error);
    }
  }

  async getUpdateListDetail() {
    this.isFormView = false;
    await this.getProviderNoteDetailsByProviderNoteId();
  }

  async getUpdateListDetailByDelete() {
    this.providerNoteDetailModel = new ProviderNoteDetailModel();
    this.clearSelectionTrigger++;

    try {
      const response = await firstValueFrom(
        this.providerNoteService.getProviderNoteById(this.providerNoteModel.providerNoteId)
      );
      if (response) {
        this.updateProviderNote.emit(response);
      }
    } catch (error) {
      console.error('Error al procesar la actualización de la lista de detalles.', error);
    }
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.providerNoteDetailModel = Object.assign(new ProviderNoteDetailModel(), event.item);
      this.isFormView = true;
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteProviderNoteDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getProviderNoteDetailsByProviderNoteId();
  }

  openRegister() {
    if (this.providerNoteModel.providerNoteId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la nota proveedor.');
      return;
    } else {
      this.isFormView = true;
    }
  }

}
